import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { FullScreen } from 'global/components/modal';
import Form from 'global/components/form';
import { Text } from 'global/components/text';
import { Button } from 'global/components/Button';
import SnackAlerts from 'global/components/snack-alerts';
import { useTaskTypes } from './useTaskTypes';
import TaskTypeFieldBuilder from './TaskTypeFieldBuilder';
import {
  DEFAULT_CLOSEOUT,
  CLOSEOUT_OPTIONS,
  DEFAULT_FIELDS,
  LOCATION_TYPE_OPTIONS,
  MULTI_SELECT_BEHAVIOR_OPTIONS,
  EMAIL_BEHAVIOR_OPTIONS,
  DEFAULT_EMAIL_BEHAVIOR,
} from './constants';
import { useTheme } from '@mui/material/styles';
import { get } from 'global/lib/lodash';
import {
  CloseoutRow,
  EditorContent,
  FloatingButtons,
  Section,
  SectionTitle,
  Subtitle,
  TaskTypeRoot,
  TT_FORM_STYLE,
} from './styles';

export const TaskTypeEditor = withRouter((props) => {
  const theme = useTheme();
  const id = get(props, 'match.params.id');
  const { data, updateTaskType, loading } = useTaskTypes();
  const taskType = data.find((tt) => tt.id === id);

  const [formState, setFormState] = useState({});
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState();

  useEffect(() => {
    if (taskType) {
      const overrides = get(taskType, 'default_field_overrides', null) || {};
      const builtIn = DEFAULT_FIELDS.map((df) => {
        const ov = overrides[df.id];
        if (ov) {
          return { ...df, is_enabled: ov.is_enabled ?? df.is_enabled, is_required: ov.is_required ?? df.is_required };
        }
        return { ...df };
      });
      const custom = get(taskType, 'custom_fields', []);
      // Check if there's a saved field_order that includes default field positions
      const fieldOrder = get(taskType, 'field_order', null);
      if (fieldOrder && Array.isArray(fieldOrder)) {
        // Restore saved ordering for all fields (default + custom)
        const allFields = [...builtIn, ...custom];
        const ordered = [];
        fieldOrder.forEach((fid, idx) => {
          const found = allFields.find((f) => f.id === fid);
          if (found) ordered.push({ ...found, order: idx });
        });
        // Append any fields not in the saved order (newly added defaults)
        allFields.forEach((f) => {
          if (!ordered.find((o) => o.id === f.id)) {
            ordered.push({ ...f, order: ordered.length });
          }
        });
        setFields(ordered);
      } else {
        setFields([...builtIn, ...custom].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
    }
  }, [taskType]);

  const goBack = () => {
    props.history.push('/account/content', { from: `account/content/task-type/${id}` });
  };

  const onSave = async () => {
    const customFields = fields.filter((f) => !f.id.startsWith('f_'));
    const fieldOrder = fields.map((f) => f.id);
    // Save overrides for default fields (is_enabled / is_required changes)
    const defaultFieldOverrides = {};
    fields.filter((f) => f.id.startsWith('f_')).forEach((f) => {
      const orig = DEFAULT_FIELDS.find((df) => df.id === f.id);
      if (orig && (f.is_enabled !== orig.is_enabled || f.is_required !== orig.is_required)) {
        defaultFieldOverrides[f.id] = { is_enabled: f.is_enabled, is_required: f.is_required };
      }
    });
    const updates = {
      name: get(formState, 'name', get(taskType, 'name')),
      custom_name: get(formState, 'name', get(taskType, 'custom_name', get(taskType, 'name'))),
      location_types: get(formState, 'location_types', get(taskType, 'location_types', [])),
      multi_select_behavior: get(formState, 'multi_select_behavior', get(taskType, 'multi_select_behavior', 'one_by_one')),
      closeout_requirements: {
        ...get(formState, 'closeout_requirements', get(taskType, 'closeout_requirements', DEFAULT_CLOSEOUT)),
        status_change_email: get(formState, 'status_change_email', get(taskType, 'closeout_requirements.status_change_email', DEFAULT_EMAIL_BEHAVIOR)),
      },
      custom_fields: customFields,
      field_order: fieldOrder,
      default_field_overrides: Object.keys(defaultFieldOverrides).length ? defaultFieldOverrides : null,
    };
    try {
      setSaving(true);
      await updateTaskType(id, updates);
      goBack();
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const onFormState = (vals) => {
    setFormState((prev) => ({ ...prev, ...vals }));
  };

  if (loading || !taskType) {
    return (
      <FullScreen open={true} onClose={goBack} title={loading ? 'Loading...' : 'Task Type Not Found'}>
        <EditorContent>
          {!loading && <Text>Task type not found.</Text>}
        </EditorContent>
      </FullScreen>
    );
  }

  const title = `Configure ${get(taskType, 'name', '')}s`;

  return (
    <FullScreen open={true} onClose={goBack} title={title}>
      <TaskTypeRoot>
      <EditorContent>
        <Subtitle as={Text}>
          Setup your {get(taskType, 'name', '')} with these defaults
        </Subtitle>

        {/* General */}
        <Section>
          <SectionTitle as={Text}>General</SectionTitle>
          <Form
            showAllScreens
            formStyle={TT_FORM_STYLE}
            onFormState={onFormState}
            screens={[
              {
                inputs: [
                  {
                    id: 'name',
                    type: 'TextField',
                    defaultValue: () => get(taskType, 'name', ''),
                    props: { label: 'Name', variant: 'outlined' },
                  },
                  {
                    id: 'location_types',
                    type: 'MultiSelect',
                    defaultValue: () => get(taskType, 'location_types', []),
                    data: LOCATION_TYPE_OPTIONS,
                    item: (i) => i,
                    props: { label: 'Location Type(s)', variant: 'outlined' },
                  },
                  {
                    id: 'multi_select_behavior',
                    type: 'Select',
                    defaultValue: () => get(taskType, 'multi_select_behavior', 'one_by_one'),
                    data: MULTI_SELECT_BEHAVIOR_OPTIONS,
                    props: { label: 'Multi-Select Behavior', variant: 'outlined' },
                  },
                ],
              },
            ]}
          />
        </Section>

        {/* Fields */}
        <TaskTypeFieldBuilder fields={fields} onChange={setFields} canEdit={true} />

        {/* Status Change Requirements */}
        <Section>
          <SectionTitle as={Text}>Status Change Requirements</SectionTitle>
          <CloseoutRow>
            <Form
              showAllScreens
              formStyle={TT_FORM_STYLE}
              onFormState={onFormState}
              screens={[
                {
                  inputs: [
                    {
                      id: 'closeout_requirements',
                      type: 'Checkbox',
                      options: CLOSEOUT_OPTIONS,
                      defaultValue: () => get(taskType, 'closeout_requirements', DEFAULT_CLOSEOUT),
                      props: { row: true },
                    },
                  ],
                },
              ]}
            />
          </CloseoutRow>
        </Section>

        {/* Status Change Email */}
        <Section>
          <SectionTitle as={Text}>Status Change Email</SectionTitle>
          <Form
            showAllScreens
            formStyle={TT_FORM_STYLE}
            onFormState={onFormState}
            screens={[
              {
                inputs: [
                  {
                    id: 'status_change_email',
                    type: 'RadioButton',
                    options: EMAIL_BEHAVIOR_OPTIONS.map((opt) => ({
                      ...opt,
                      label: opt.helperText ? (
                        <div>
                          <div>{opt.label}</div>
                          <div style={{ fontSize: 12, color: theme.palette.text.secondary }}>{opt.helperText}</div>
                        </div>
                      ) : opt.label,
                    })),
                    defaultValue: () => get(taskType, 'closeout_requirements.status_change_email', DEFAULT_EMAIL_BEHAVIOR),
                  },
                ],
              },
            ]}
          />
        </Section>
      </EditorContent>

      {/* Floating action buttons */}
      <FloatingButtons>
        <Button variant="text" onClick={goBack}>
          Cancel
        </Button>
        <Button color="primary" onClick={onSave} disabled={saving}>
          Save
        </Button>
      </FloatingButtons>
      </TaskTypeRoot>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </FullScreen>
  );
});
